import { useCallback } from 'react';
import type { Observable } from 'rxjs';
import { useSecuritiesContext } from '../contexts';
import { MARKET_DATA_SNAPSHOT } from '../tokens';
import {
  DepthTypeEnum,
  FeeModeEnum,
  LiquidityTypeEnum,
  type MarketDataSnapshot,
  type MarketDataSnapshotRequest,
  type SubscriptionResponse,
} from '../types';
import { EMPTY_ARRAY } from '../utils';
import { useMultiMarketDataSnapshotBase } from './useMultiMarketDataSnapshotBase';
import { useSyncedRef } from './useSyncedRef';

/**
 * We can have multiple active MDS subscriptions for the same Symbol (for example, different Markets selections).
 * `requestKey` preserves which logical request produced the snapshot so downstream consumers can disambiguate/cache correctly.
 */
export interface KeyedMarketDataSnapshot extends MarketDataSnapshot {
  requestKey: string;
}

export interface UseMultiMarketDataSnapshotOutput {
  cache: Map<string, KeyedMarketDataSnapshot>;
  json: SubscriptionResponse<KeyedMarketDataSnapshot>;
}

export type UseMultiMarketDataSnapshotRequestParams = Pick<
  MarketDataSnapshotRequest,
  'SizeBuckets' | 'Depth' | 'PriceIncrement'
>;

export interface UseMultiMarketDataSnapshotRequest {
  /** Logical cache/subscription key, usually produced by `getMultiMarketDataSnapshotRequestKey`. */
  key: string;
  Symbol: string;
  Markets?: string[];
  SizeBuckets?: UseMultiMarketDataSnapshotRequestParams['SizeBuckets'];
  Depth?: UseMultiMarketDataSnapshotRequestParams['Depth'];
  PriceIncrement?: UseMultiMarketDataSnapshotRequestParams['PriceIncrement'];
}

/**
 * Builds the logical request key used by `useMultiMarketDataSnapshot`.
 *
 * The key includes every input that changes the backend MDS stream: symbol, optional market filters,
 * and optional size buckets. The base hook uses this same key to dedupe subscriptions, cache the
 * latest snapshot, and expose `requestKey` on emitted data.
 */
export function getMultiMarketDataSnapshotRequestKey({
  Symbol: symbol,
  Markets: markets,
  SizeBuckets: sizeBuckets,
  Depth: depth,
  PriceIncrement: priceIncrement,
}: Pick<UseMultiMarketDataSnapshotRequest, 'Symbol' | 'Markets' | 'SizeBuckets' | 'Depth' | 'PriceIncrement'>) {
  return [
    symbol,
    ...getMarketDataSnapshotRequestKeyMarketPart({ Markets: markets }),
    ...(sizeBuckets?.length ? [`sizeBuckets:${sizeBuckets.join(',')}`] : EMPTY_ARRAY),
    ...(depth != null ? [`depth:${depth}`] : EMPTY_ARRAY),
    ...(priceIncrement != null ? [`priceIncrement:${priceIncrement}`] : EMPTY_ARRAY),
  ]
    .filter(Boolean)
    .join('|');
}

function getMarketDataSnapshotRequestKeyMarketPart({
  Markets: markets,
}: Pick<UseMultiMarketDataSnapshotRequest, 'Markets'>) {
  if (markets) {
    return markets.length > 0 ? [...markets].toSorted() : ['markets:'];
  }

  return EMPTY_ARRAY;
}

function getMarketName(marketOrMarketAccount: string) {
  return marketOrMarketAccount.split('/')[0];
}

/** Request markets can be a greater set than that defined by the security we are making the request for. We return the intersection so we only make valid MDS requests. */
function intersectMarkets({
  requestMarkets,
  securityMarkets,
}: {
  requestMarkets: string[] | undefined;
  securityMarkets: string[];
}) {
  if (requestMarkets == null) {
    return securityMarkets;
  }

  const securityMarketSet = new Set(securityMarkets);
  return requestMarkets.filter(marketOrMarketAccount => securityMarketSet.has(getMarketName(marketOrMarketAccount)));
}

interface UseMultiMarketDataParams {
  /** One request entry per MDS stream. This should be a ReplaySubject. */
  requestsObs: Observable<UseMultiMarketDataSnapshotRequest[]>;
  /** The maximum amount of subscriptions we dare keep open with the backend */
  maxActiveSubscriptions: number;
  /** The tag to put on the subscription for better debugging of all related streams to the hook invocation */
  subscriptionTag?: string;
  showFirmLiquidity?: boolean;
  showAllInPrices?: boolean;

  /** Pass in overrides to apply to _every_ MDS request */
  requestOverrides?: Partial<MarketDataSnapshotRequest>;
}

function getMarketDataRequestKey(request: UseMultiMarketDataSnapshotRequest) {
  return request.key;
}

/**
 * Subscribes to standard MDS streams for many logical requests.
 *
 * This hook owns standard market-data request shaping, including market intersection and liquidity
 * flags. It delegates websocket lifecycle/cache behavior to `useMultiMarketDataSnapshotBase`, where
 * `request.key` becomes the subscription identity and emitted snapshot `requestKey`.
 */
export const useMultiMarketDataSnapshot = ({
  requestsObs,
  maxActiveSubscriptions,
  subscriptionTag,
  showFirmLiquidity,
  showAllInPrices,

  requestOverrides,
}: UseMultiMarketDataParams) => {
  const { securitiesBySymbol } = useSecuritiesContext();

  /**
   * We just make this a synced ref so we dont need to handle with the case where securitiesBySymbol updates in the pipe. Its
   * fine if we fall slightly out of date. In the worst case, a refresh will always fix any problem the user has.
   */
  const securitiesBySymbolRef = useSyncedRef(securitiesBySymbol);

  const buildSubscriptionRequest = useCallback(
    ({
      Symbol: symbol,
      Markets: markets,
      SizeBuckets: sizeBuckets,
      Depth: depth,
      PriceIncrement: priceIncrement,
    }: UseMultiMarketDataSnapshotRequest): MarketDataSnapshotRequest | undefined => {
      let request: MarketDataSnapshotRequest | undefined;

      if (symbol) {
        const securityMarkets = securitiesBySymbolRef.current.get(symbol)?.Markets ?? EMPTY_ARRAY;
        const hasSizeBuckets = Boolean(sizeBuckets?.length);
        const { Depth: _Depth, Markets: _Markets, ...requestOverridesWithoutDepthAndMarkets } = requestOverrides ?? {};
        const requestMarkets =
          markets == null
            ? (requestOverrides?.Markets ?? securityMarkets)
            : intersectMarkets({ requestMarkets: markets, securityMarkets });
        request = {
          ...(hasSizeBuckets ? requestOverridesWithoutDepthAndMarkets : requestOverrides),
          ...(hasSizeBuckets
            ? { SizeBuckets: sizeBuckets }
            : {
                Depth: depth ?? requestOverrides?.Depth ?? 3,
              }),
          name: MARKET_DATA_SNAPSHOT,
          DepthType: DepthTypeEnum.Price,
          PriceIncrement:
            priceIncrement ??
            requestOverrides?.PriceIncrement ??
            securitiesBySymbolRef.current.get(symbol)?.DefaultPriceIncrement,
          Symbol: symbol,
          tag: subscriptionTag ?? 'MultiMarketDataSnapshot',
          Markets: requestMarkets,
          LiquidityType: showFirmLiquidity ? LiquidityTypeEnum.Firm : LiquidityTypeEnum.Indicative,
          FeeMode: showAllInPrices ? FeeModeEnum.Taker : undefined,
        };
      }

      return request;
    },
    [subscriptionTag, securitiesBySymbolRef, showFirmLiquidity, showAllInPrices, requestOverrides]
  );

  return useMultiMarketDataSnapshotBase({
    requestsObs,
    maxActiveSubscriptions,
    getRequestKey: getMarketDataRequestKey,
    buildSubscriptionRequest,
  });
};
